import React, { useEffect, useState } from 'react';
import {
  useMediaQuery,
  DialogContent,
  DialogTitle,
  Dialog,
  DialogActions,
  Button,
  Box,
} from '@mui/material';
import { Checkbox } from '@sm-dashboard/ui-components';
import { CustomSlider } from '@sm-dashboard/form-provider';
import {
  Autocomplete,
  DatePicker,
  HebrewDatePicker,
  Input,
  Select,
  gregorianToHebrewString,
  type SelectOption
} from '@sm-dashboard/ui-components';
import { FilterConfig, FilterOptionType } from '@sm-dashboard/types';
import { useTheme } from '@mui/material/styles';
import { useSearchParams } from 'react-router-dom';
import { sourceOptions, statusArr } from './filterOptions';

const PANEL_FIELD_WIDTH = 140;
const PANEL_WIDE_WIDTH = 180;
const PANEL_FIELD_HEIGHT = 42;

type Props<T> = {
  filter: FilterConfig<T>;
  handleApplyFilters: (filter: FilterConfig<any>, value: any) => void;
};

const parseDate = (value: string | null | undefined): Date | null => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatDate = (value: Date | null): string | null => {
  if (!value) return null;
  const y = value.getFullYear();
  const m = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const flattenOptions = (options: FilterOptionType[], parentValue?: string): SelectOption[] => {
  const result: SelectOption[] = [];
  for (const option of options) {
    const segment = String(option.value);
    const fullValue = parentValue ? `${parentValue}-${segment}` : segment;
    if (option.options?.length) {
      result.push(...flattenOptions(option.options, segment));
    } else {
      result.push({ label: option.label || option.name || segment, value: fullValue });
    }
  }
  return result;
};

const handleFilterChange = (
  filter: FilterConfig<any>,
  value: any,
  handleApplyFilters: (filter: FilterConfig<any>, value: any) => void
) => {
  if (filter.type === 'multiSelect' && Array.isArray(value)) {
    const stringArr = value.map(String);
    const unique = stringArr.filter((item, index) => stringArr.indexOf(item) === index);
    handleApplyFilters(filter, unique);
    return;
  }
  handleApplyFilters(filter, value);
};

/** Collapsible filter panel only — design-system fields (`appearance="plain"`). */
export const FilterPanelInput = <T,>({ filter, handleApplyFilters }: Props<T>): React.ReactElement | null => {
  const [searchParams] = useSearchParams();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogValue, setDialogValue] = useState<FilterOptionType | null>(null);
  const [choosedValue, setChoosedValue] = useState<FilterOptionType | null>(null);
  const [textValue, setTextValue] = useState(
    () => searchParams.get(filter.field as string) || filter.defaultValue || ''
  );

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const fieldWidth = isSmallScreen
    ? '100%'
    : filter.width ??
      (filter.type === 'date' || filter.type === 'hebrewDate' || filter.type === 'autocomplete'
        ? PANEL_WIDE_WIDTH
        : PANEL_FIELD_WIDTH);

  const wrap = (node: React.ReactElement) => (
    <Box
      sx={{
        width: fieldWidth,
        flexShrink: isSmallScreen ? 1 : 0,
        maxWidth: '100%',
        height: PANEL_FIELD_HEIGHT,
        overflow: filter.type === 'hebrewDate' ? 'visible' : undefined,
        '& .MuiFormControl-root, & .MuiTextField-root, & .MuiAutocomplete-root': {
          height: '100%',
          margin: 0
        },
        '& .SmInputBase-root': {
          height: '100%'
        }
      }}
    >
      {node}
    </Box>
  );

  const apply = (value: any) => handleFilterChange(filter, value, handleApplyFilters);

  useEffect(() => {
    const urlValue = searchParams.get(filter.field as string);
    if (filter.type === 'text' || filter.type === 'number' || filter.type === undefined) {
      setTextValue(urlValue || filter.defaultValue || '');
    }
  }, [searchParams, filter.field, filter.type, filter.defaultValue]);

  const [debounceTimer, setDebounceTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  const debouncedApply = (value: any) => {
    if (debounceTimer) clearTimeout(debounceTimer);
    setDebounceTimer(setTimeout(() => apply(value), 1000));
  };
  useEffect(
    () => () => {
      if (debounceTimer) clearTimeout(debounceTimer);
    },
    [debounceTimer]
  );

  if ((filter.type === 'select' || filter.type === 'multiSelect') && !filter.options) {
    return null;
  }

  const sortOptions = (options: FilterOptionType[]) =>
    [...options].sort((a, b) => {
      if ((!a.options || !a.options.length) && b.options?.length) return -1;
      if (a.options?.length && (!b.options || !b.options.length)) return 1;
      return 0;
    });

  switch (filter.type) {
    case 'date':
      return wrap(
        <DatePicker
          appearance="plain"
          placeholder={filter.label}
          value={parseDate(searchParams.get(filter.field as string) || filter.defaultValue)}
          onChange={d => apply(formatDate(d))}
          fullWidth
        />
      );

    case 'hebrewDate': {
      const gregorian = searchParams.get(filter.field as string) || filter.defaultValue || '';
      return wrap(
        <HebrewDatePicker
          appearance="plain"
          placeholder={filter.label}
          hebrewDate={gregorianToHebrewString(gregorian) || ''}
          gregorianDate={gregorian}
          onHebrewDateChange={next => {
            if (!next) apply(null);
          }}
          onGregorianDateChange={date => apply(date || null)}
          fullWidth
        />
      );
    }

    case 'text':
    case 'number':
    case undefined:
      return wrap(
        <Input
          appearance="plain"
          placeholder={filter.label}
          type={filter.type === 'number' ? 'number' : 'text'}
          value={textValue}
          onChange={e => {
            setTextValue(e.target.value);
            debouncedApply(e.target.value);
          }}
          fullWidth
        />
      );

    case 'autocomplete': {
      const paramValue = searchParams.get(filter.field as string) || filter.defaultValue;
      const selected =
        (filter.options || []).find(o => String(o.value) === String(paramValue) || o.value === paramValue) ?? null;
      return wrap(
        <Autocomplete
          appearance="plain"
          placeholder={filter.label}
          options={filter.options || []}
          value={selected}
          onChange={(_, value) => apply(value)}
          getOptionLabel={o => o?.label || o?.name || ''}
          isOptionEqualToValue={(a, b) => !!a && !!b && a.value === b.value}
          fullWidth
        />
      );
    }

    case 'select':
    case 'status':
    case 'sourceSelect': {
      const options = filter.type === 'sourceSelect' ? filter.options ?? sourceOptions : filter.options || statusArr;
      const raw = searchParams.get(filter.field as string) || filter.defaultValue || '';
      return wrap(
        <Select
          appearance="plain"
          placeholder={filter.label}
          value={raw ? String(raw) : undefined}
          onChange={v => apply(v || null)}
          options={flattenOptions(sortOptions(options))}
        />
      );
    }

    case 'multiSelect': {
      const options = filter.options || [];
      let value: string | string[] = searchParams.get(filter.field as string) || filter.defaultValue || '';
      if (!Array.isArray(value)) value = value ? String(value).split(',') : [];

      return wrap(
        <Select
          appearance="plain"
          placeholder={filter.label}
          multiple
          value={value}
          onChange={apply}
          options={sortOptions(options).map(opt => ({ value: String(opt.value || opt.label), label: opt.label || opt.name }))}
        />
      );
    }

    case 'checkbox':
      return (
        <Box
          sx={{
            flexShrink: 0,
            alignSelf: 'center',
            marginInlineStart: 1,
            ...(!isSmallScreen && { transform: 'translateY(-10px)' })
          }}
        >
          <Checkbox
            size="small"
            checked={searchParams.get(filter.field as string) === 'true'}
            onChange={(_, checked) => apply(checked)}
            label={filter.label}
          />
        </Box>
      );

    case 'sliderRange':
      return (
        <Box
          sx={{
            width: isSmallScreen ? '100%' : filter.width ?? 400,
            minWidth: isSmallScreen ? undefined : filter.width ?? 400,
            flex: isSmallScreen ? undefined : '1 1 auto',
            maxWidth: '100%',
            marginInlineEnd: isSmallScreen ? 0 : 3,
            flexShrink: 0,
            alignSelf: 'center',
            ...(isSmallScreen && {
              display: 'flex',
              justifyContent: 'center',
              '& > div': { mx: 0, width: '100%', maxWidth: 360 }
            })
          }}
        >
          <CustomSlider
            onChange={v => apply(v)}
            defaultValue={searchParams.get(filter.field as string)?.split(',') || filter.defaultValue || []}
            label={filter.label}
            min={filter.min}
            max={filter.max}
            width="100%"
            labelPosition="left"
          />
        </Box>
      );

    case 'custom':
      return wrap(
        <>
          {filter.render?.({
            value: searchParams.get(filter.field as string),
            onChange: apply,
            placeholder: filter.label
          })}
        </>
      );

    case 'dialog':
      return (
        <>
          <Button variant="outlined" onClick={() => setDialogOpen(true)}>
            {filter.label}
            {choosedValue ? `: ${choosedValue.label || choosedValue.name}` : ''}
          </Button>
          <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
            <DialogTitle>{filter.label}</DialogTitle>
            <DialogContent>
              {filter.dialog && React.createElement(filter.dialog, { setState: setDialogValue })}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setDialogValue(null);
                  setDialogOpen(false);
                }}
              >
                ביטול
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  setChoosedValue(dialogValue);
                  apply(dialogValue?.value);
                  setDialogOpen(false);
                }}
              >
                אישור
              </Button>
            </DialogActions>
          </Dialog>
        </>
      );

    default:
      return null;
  }
};
